import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { AdminService } from '../../admin/admin.service';
import { DataRulesConfirmDialogComponent } from '../../data-rules/data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { DuplicateService } from '../../duplikit/duplicate.service';
import { DataToolsApps, DataToolsAppsTitles } from '../../shared/DataToolsApps';
import { DuplikitMergeService } from '../../shared/services/duplikit-merge.service';
import { ScheduledMergesService } from '../../shared/services/scheduled-merges.service';
import { DataToolsSettingsService } from '../data-tools-settings.service';
import { ScheduledDedupeSettingsCommand } from '../duplikit-settings.model';
import { MasterIdentificationOptionsComponent } from '../master-identification-options/master-identification-options.component';
import { ScheduledMergesComponent } from '../scheduled-merges/scheduled-merges.component';
import { Options } from './duplikit-options-config';
import { DuplikitOptionsService } from './duplikit-options.service';

@Component({
  selector: 'app-duplikit-options',
  templateUrl: './duplikit-options.component.html',
  imports: [
    NgClass,
    MatTooltip,
    MatSlideToggle,
    FormsModule,
    NgForOf,
    NgIf,
    ScheduledMergesComponent,
    MasterIdentificationOptionsComponent
  ],
  styleUrls: ['./duplikit-options.component.scss']
})
export class DuplikitOptionsComponent implements OnInit, OnDestroy {

  @Output()
  needsSavingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  needSaving = false;
  saving = false;
  placeholder = 'Select...';
  options: Options = new Options;
  scheduledMergesEnabled = false;

  // Collapsing Sections
  toggleCandidatesOptions = true;
  toggleContactOptions = true;
  toggleCompaniesOptions = true;
  toggleMasterIdentification = true;
  loadMasterIdentification = false;
  toggleScheduledMerges = true;
  loadScheduledMerges = false;

  DataToolsApps = DataToolsApps;
  DataToolsAppsTitles = DataToolsAppsTitles;

  validateTooltip = 'Click to validate custom query';

  customQueryData = {
    Candidate: {
      defaultQuery: 'isDeleted: false AND NOT status: "Archive" AND NOT firstName: \"****\" AND NOT lastName: \"****\"',
      queryHasChanged: false,
      validatingCustomQuery: false,
      recordsFound: null
    },
    ClientContact: {
      defaultQuery: 'isDeleted=false AND status <> \'Archive\' AND firstName <> \'****\' AND lastName <> \'****\' AND clientCorporation.status <> \'Archive\'',
      queryHasChanged: false,
      validatingCustomQuery: false,
      recordsFound: null
    },
    ClientCorporation: {
      defaultQuery: 'status <> \'Archive\'',
      queryHasChanged: false,
      validatingCustomQuery: false,
      recordsFound: null
    }
  };

  constructor(
    private duplikitOptionsService: DuplikitOptionsService,
    public router: Router,
    private dialog: MatDialog,
    private duplicateService: DuplicateService,
    private scheduledMergesService: ScheduledMergesService,
    private duplikitMergeService: DuplikitMergeService,
    private adminService: AdminService,
    public dataToolsSettingsService: DataToolsSettingsService,
  ) {
    this.duplikitOptionsService.getOptions().subscribe( options => {
      this.options = options;
    });
  }

  ngOnInit() {
    if (!this.adminService.isInLiteMode()) {
      this.duplikitMergeService.runMergeCheck();
    }

    this.scheduledMergesService.getScheduledDedupeSettingsForCorp().subscribe((res) => {
      this.scheduledMergesEnabled = res.enabled;
    });

    this.duplikitOptionsService.masterIdentificationHasChanged.candidate = new Subject();
    this.duplikitOptionsService.masterIdentificationHasChanged.candidate.subscribe(() => {
      this.needsSaving();
    });

    this.duplikitOptionsService.masterIdentificationHasChanged.clientContact = new Subject();
    this.duplikitOptionsService.masterIdentificationHasChanged.clientContact.subscribe(() => {
      this.needsSaving();
    });

    this.duplikitOptionsService.masterIdentificationHasChanged.clientCorporation = new Subject();
    this.duplikitOptionsService.masterIdentificationHasChanged.clientCorporation.subscribe(() => {
      this.needsSaving();
    });

    this.scheduledMergesService.changesMade = new Subject();
    this.scheduledMergesService.changesMade.subscribe(() => {
      this.needsSaving();
    });
  }

  locationsMerge(): void {
    if (this.options.locationsMergeCompany) {
      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          titleIcon: 'bhi-caution',
          title: 'Are you sure?',
          messageIcon: 'bhi-location',
          message: 'The merging of Locations is irreversible when the merge is reverted'
        }
      });

      dialogRef.afterClosed().subscribe((data) => {
        if (!data) {
          this.options.locationsMergeCompany = false;
          return;
        }
        this.needsSaving();
      });
    }
  }

  needsSaving(): void {
    this.needSaving = true;
    this.saving = false;
    this.needsSavingEmitter.emit(this.needSaving);
  }

  queryChanged(entity: string): void {
    this.customQueryData[entity].queryHasChanged = true;
    this.customQueryData[entity].recordsFound = null;

    if (this.getCustomQueryForEntity(entity) === '') {
      this.customQueryData[entity].queryHasChanged = false;
      this.needSaving = true;
    }

    this.dataToolsSettingsService.syncRequired.next(entity);
    this.needsSaving();
  }

  validateCustomQuery(entity: string, queryString: string): void {
    this.customQueryData[entity].validatingCustomQuery = true;
    this.customQueryData[entity].recordsFound = null;

    this.duplicateService.validateCustomQuery(entity, queryString).subscribe((res) => {
      this.customQueryData[entity].recordsFound = res;
      this.customQueryData[entity].validatingCustomQuery = false;
      this.customQueryData[entity].queryHasChanged = false;
    });
  }

  customQueryCanBeValidated(entity: string): boolean {
    const query = this.getCustomQueryForEntity(entity);

    return query && this.customQueryData[entity].queryHasChanged && !this.customQueryData[entity].validatingCustomQuery;
  }

  getCustomQueryForEntity(entity: string): string {
    switch (entity) {
      case 'Candidate':
        return this.options.candidateCustomQuery;
      case 'ClientContact':
        return this.options.contactCustomQuery;
      case 'ClientCorporation':
        return this.options.companyCustomQuery;
    }
  }

  formHasValidCustomQueries() {
    return this.customQueryData.Candidate.recordsFound !== 0
        && !this.customQueryData.Candidate.queryHasChanged
        && this.customQueryData.ClientContact.recordsFound !== 0
        && !this.customQueryData.ClientContact.queryHasChanged
        && this.customQueryData.ClientCorporation.recordsFound !== 0
        && !this.customQueryData.ClientCorporation.queryHasChanged;
  }

  ngOnDestroy() {
    this.duplikitOptionsService.masterIdentificationHasChanged.candidate.unsubscribe();
    this.duplikitOptionsService.masterIdentificationHasChanged.clientContact.unsubscribe();
    this.scheduledMergesService.changesMade.unsubscribe();
    this.duplikitMergeService.stopMergeCheck();
  }

  get isInLiteMode() {
    return this.adminService.isInLiteMode();
  }

  get scheduledDedupeSettings(): ScheduledDedupeSettingsCommand {
    if (!this.scheduledMergesService.scheduledDedupeSettings) {
      return undefined;
    }
    return {
      corpId: this.scheduledMergesService.scheduledDedupeSettings.corpId,
      enabledEntities: this.scheduledMergesService.getEnabledEntitiesAsString(),
      enabledDays: this.scheduledMergesService.getEnabledDaysAsString(),
      mergeTimeUTC: this.scheduledMergesService.scheduledDedupeSettings.mergeTimeUTC,
      batchSize: this.scheduledMergesService.scheduledDedupeSettings.batchSize
    };
  }
}
